import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enGB } from 'date-fns/locale';
import { addDays, addMonths, format, isWeekend, getDay, isSameDay, parseISO } from 'date-fns';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import BuildIcon from '@mui/icons-material/Build';
import bookingPublicService from '../../services/bookingPublicService';

// UK Bank Holidays 2025-2027
const UK_BANK_HOLIDAYS = [
  // 2025
  '2025-01-01', // New Year's Day
  '2025-04-18', // Good Friday
  '2025-04-21', // Easter Monday
  '2025-05-05', // Early May Bank Holiday
  '2025-05-26', // Spring Bank Holiday
  '2025-08-25', // Summer Bank Holiday
  '2025-12-25', // Christmas Day
  '2025-12-26', // Boxing Day
  // 2026
  '2026-01-01', // New Year's Day
  '2026-04-03', // Good Friday
  '2026-04-06', // Easter Monday
  '2026-05-04', // Early May Bank Holiday
  '2026-05-25', // Spring Bank Holiday
  '2026-08-31', // Summer Bank Holiday
  '2026-12-25', // Christmas Day
  '2026-12-28', // Boxing Day (substitute - 26th is Saturday)
  // 2027
  '2027-01-01', // New Year's Day
  '2027-03-26', // Good Friday
  '2027-03-29', // Easter Monday
  '2027-05-03', // Early May Bank Holiday
  '2027-05-31', // Spring Bank Holiday
  '2027-08-30', // Summer Bank Holiday
  '2027-12-27', // Christmas Day (substitute - 25th is Saturday)
  '2027-12-28', // Boxing Day (substitute - 26th is Sunday)
];

const isUkBankHoliday = (date) => {
  const dateStr = format(date, 'yyyy-MM-dd');
  return UK_BANK_HOLIDAYS.includes(dateStr);
};

// Get next available booking day (skip weekends, bank holidays, and blocked dates based on settings)
const getNextAvailableDay = (date, blockWeekends = true, blockBankHolidays = true, blockedDates = []) => {
  let nextDate = date;
  let attempts = 0;
  const maxAttempts = 365; // Safety limit

  while (attempts < maxAttempts) {
    const shouldSkip =
      (blockWeekends && isWeekend(nextDate)) ||
      (blockBankHolidays && isUkBankHoliday(nextDate)) ||
      blockedDates.some(bd => isSameDay(parseISO(bd), nextDate));

    if (!shouldSkip) return nextDate;
    nextDate = addDays(nextDate, 1);
    attempts++;
  }
  return nextDate;
};

const PublicBookingForm = () => {
  const { orgCode } = useParams();
  const navigate = useNavigate();

  // Organization config
  const [orgConfig, setOrgConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form data
  const [services, setServices] = useState([]);
  const [locations, setLocations] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);

  // Default time slots (fallback if none configured)
  const defaultTimeSlots = [
    { code: 'morning', label: 'Morning (06:45 - 08:30)' },
    { code: 'afternoon', label: 'Afternoon' },
    { code: 'all_day', label: 'All Day' },
  ];
  const [selectedServices, setSelectedServices] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    registration: '',
    make: '',
    model: '',
    fuelType: '',
    year: '',
    motExpiry: '',      // MOT expiry date from DVLA
    motStatus: '',      // MOT status from DVLA
    bookingDate: addDays(new Date(), 1), // Will be adjusted after config loads
    timeOfDay: 'morning',
    location: '',
    lockerRequested: true,
    notes: '',
    termsAccepted: false,
  });

  // UI state
  const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false);
  const [vehicleLookupDone, setVehicleLookupDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Load organization config, services, and locations
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [configRes, servicesRes, locationsRes, timeSlotsRes] = await Promise.all([
          bookingPublicService.getOrgConfig(orgCode),
          bookingPublicService.getServices(orgCode).catch(() => ({ services: [] })),
          bookingPublicService.getLocations(orgCode).catch(() => ({ locations: [] })),
          bookingPublicService.getTimeSlots(orgCode).catch(() => ({ time_slots: [] })),
        ]);

        setOrgConfig(configRes);
        setServices(servicesRes.services || []);
        setLocations(locationsRes.locations || []);
        setTimeSlots(timeSlotsRes.time_slots || []);

        // Set defaults
        const newFormData = { ...formData };

        // Set default booking date based on config (skip weekends and bank holidays if blocked)
        const blockWeekends = configRes.booking_block_weekends ?? true;
        const blockBankHolidays = configRes.booking_block_bank_holidays ?? true;
        const blockedDates = configRes.blocked_dates || [];
        newFormData.bookingDate = getNextAvailableDay(
          addDays(new Date(), 1),
          blockWeekends,
          blockBankHolidays,
          blockedDates
        );

        // Set default location if only one
        if (locationsRes.locations?.length === 1) {
          newFormData.location = locationsRes.locations[0].code;
        }

        // Set default time slot if available
        const slots = timeSlotsRes.time_slots || [];
        if (slots.length > 0) {
          newFormData.timeOfDay = slots[0].code;
        }

        setFormData(prev => ({ ...prev, ...newFormData }));
      } catch (err) {
        console.error('Failed to load booking config:', err);
        setError(err.response?.data?.error || 'Failed to load booking form');
      } finally {
        setLoading(false);
      }
    };

    if (orgCode) {
      loadData();
    }
  }, [orgCode]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle DVLA lookup
  const handleVehicleLookup = async () => {
    if (!formData.registration.trim()) return;

    try {
      setVehicleLookupLoading(true);
      const result = await bookingPublicService.lookupVehicle(formData.registration);

      if (result.found && result.vehicle) {
        setFormData(prev => ({
          ...prev,
          registration: result.vehicle.registration || prev.registration,
          make: result.vehicle.make || prev.make,
          model: result.vehicle.model || prev.model,
          fuelType: result.vehicle.fuel_type || prev.fuelType,
          // DVLA API returns yearOfManufacture as 'year', fallback to first_registration parsing
          year: result.vehicle.year ||
                result.vehicle.first_registration?.split('-')[0] ||
                prev.year,
          // MOT data from DVLA
          motExpiry: result.vehicle.mot_expiry || prev.motExpiry,
          motStatus: result.vehicle.mot_status || prev.motStatus,
        }));
        setVehicleLookupDone(true);
      }
    } catch (err) {
      console.error('Vehicle lookup failed:', err);
    } finally {
      setVehicleLookupLoading(false);
    }
  };

  // Calculate total
  const calculateTotal = () => {
    return selectedServices.reduce((sum, svc) => sum + (svc.price || 0), 0);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (selectedServices.length === 0) {
      setSubmitError('Please select at least one service');
      return;
    }

    // Validate location if locations exist
    if (locations.length > 0 && !formData.location) {
      setSubmitError('Please select a location');
      return;
    }

    try {
      setSubmitting(true);

      const bookingData = {
        customer: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
        },
        vehicle: {
          registration: formData.registration.toUpperCase().replace(/\s/g, ''),
          make: formData.make,
          model: formData.model,
          fuel_type: formData.fuelType,
          year: formData.year ? parseInt(formData.year) : null,
          mot_expiry: formData.motExpiry || null,
          mot_status: formData.motStatus || null,
        },
        booking: {
          date: format(formData.bookingDate, 'yyyy-MM-dd'),
          time_of_day: formData.timeOfDay,
          location: formData.location,
        },
        services: selectedServices.map(svc => ({
          service_id: svc.id,
          name: svc.name,
          price: svc.price,
        })),
        notes: formData.notes,
        locker_requested: formData.lockerRequested,
      };

      const result = await bookingPublicService.createBooking(orgCode, bookingData);

      if (result.success) {
        // Find selected location and time slot details
        const selectedLocation = locations.find(l => l.code === formData.location);
        const selectedTimeSlot = timeSlots.find(t => t.code === formData.timeOfDay) ||
          defaultTimeSlots.find(t => t.code === formData.timeOfDay);

        navigate(`/book/${orgCode}/success`, {
          state: {
            // Organization
            orgName: orgConfig.name,
            orgLogo: orgConfig.logo_url,
            primaryColor: orgConfig.primary_color,
            // Booking
            bookingId: result.booking_id,
            bookingRef: result.booking_ref,
            bookingDate: format(formData.bookingDate, 'EEEE, d MMMM yyyy'),
            timeSlot: selectedTimeSlot?.label || selectedTimeSlot?.name || formData.timeOfDay,
            // Location
            locationName: selectedLocation?.name,
            locationAddress: selectedLocation?.address,
            lockerRequested: formData.lockerRequested,
            lockerInstructions: selectedLocation?.locker_instructions,
            // Customer
            customerName: `${formData.firstName} ${formData.lastName}`,
            customerEmail: formData.email,
            customerPhone: formData.phone,
            // Vehicle
            vehicleReg: formData.registration.toUpperCase(),
            vehicleMake: formData.make,
            vehicleModel: formData.model,
            vehicleYear: formData.year,
            vehicleFuelType: formData.fuelType,
            // Services
            services: selectedServices,
            total: calculateTotal(),
            // Notes
            notes: formData.notes,
          },
        });
      }
    } catch (err) {
      console.error('Booking submission failed:', err);
      setSubmitError(
        err.response?.data?.error ||
        err.response?.data?.details?.join(', ') ||
        'Failed to create booking. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state - organization not found or booking disabled
  if (error || !orgConfig?.booking_enabled) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Booking Unavailable
          </Typography>
          <Typography color="text.secondary">
            {error || 'Online booking is currently not available for this organization.'}
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          py: 4,
        }}
      >
        <Container maxWidth="md">
          {/* Header */}
          <Box textAlign="center" mb={4}>
            {orgConfig.logo_url && (
              <Box mb={2}>
                <img
                  src={orgConfig.logo_url}
                  alt={orgConfig.name}
                  style={{ maxHeight: 80, maxWidth: 200 }}
                />
              </Box>
            )}
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {orgConfig.booking_page_title || `Book with ${orgConfig.name}`}
            </Typography>
            {orgConfig.booking_page_message && (
              <Typography color="text.secondary">
                {orgConfig.booking_page_message}
              </Typography>
            )}
          </Box>

          <form onSubmit={handleSubmit}>
            {/* Customer Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon sx={{ mr: 1, color: orgConfig.primary_color }} />
                <Typography variant="h6">Your Details</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Vehicle Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <DirectionsCarIcon sx={{ mr: 1, color: orgConfig.primary_color }} />
                <Typography variant="h6">Vehicle Details</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Registration Number"
                    name="registration"
                    value={formData.registration}
                    onChange={handleChange}
                    placeholder="e.g., AB12 CDE"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleVehicleLookup}
                            disabled={vehicleLookupLoading || !formData.registration.trim()}
                            title="Lookup vehicle details"
                          >
                            {vehicleLookupLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SearchIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    helperText="Enter your registration and click the search icon to auto-fill vehicle details"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Make"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    placeholder="e.g., Ford"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="e.g., Focus"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Fuel Type"
                    name="fuelType"
                    value={formData.fuelType}
                    onChange={handleChange}
                    placeholder="e.g., Petrol"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Year"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    placeholder="e.g., 2020"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Booking Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <EventIcon sx={{ mr: 1, color: orgConfig.primary_color }} />
                <Typography variant="h6">Booking Details</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Booking Date"
                    value={formData.bookingDate}
                    onChange={(newValue) => setFormData(prev => ({ ...prev, bookingDate: newValue }))}
                    minDate={addDays(new Date(), 1)}
                    maxDate={addMonths(new Date(), 3)}
                    shouldDisableDate={(date) => {
                      // Block weekends if setting is enabled (default true)
                      if ((orgConfig?.booking_block_weekends ?? true) && isWeekend(date)) return true;
                      // Block UK bank holidays if setting is enabled (default true)
                      if ((orgConfig?.booking_block_bank_holidays ?? true) && isUkBankHoliday(date)) return true;
                      // Block dates from organization settings
                      if (orgConfig?.blocked_dates?.length > 0) {
                        return orgConfig.blocked_dates.some(bd =>
                          isSameDay(parseISO(bd), date)
                        );
                      }
                      return false;
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Time of Day</InputLabel>
                    <Select
                      name="timeOfDay"
                      value={formData.timeOfDay}
                      onChange={handleChange}
                      label="Time of Day"
                    >
                      {(timeSlots.length > 0 ? timeSlots : defaultTimeSlots).map((slot) => (
                        <MenuItem key={slot.code} value={slot.code}>
                          {slot.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {locations.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Location</InputLabel>
                      <Select
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        label="Location"
                      >
                        {locations.map((loc) => (
                          <MenuItem key={loc.code} value={loc.code}>
                            {loc.name} - {loc.address}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.lockerRequested}
                        onChange={handleChange}
                        name="lockerRequested"
                      />
                    }
                    label="I will use the key drop-off locker"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Services */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <BuildIcon sx={{ mr: 1, color: orgConfig.primary_color }} />
                <Typography variant="h6">Services Required</Typography>
              </Box>
              <Autocomplete
                multiple
                options={services}
                getOptionLabel={(option) => `${option.name} - £${option.price.toFixed(2)}`}
                value={selectedServices}
                onChange={(_, newValue) => setSelectedServices(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Services"
                    placeholder="Start typing to search..."
                    required={selectedServices.length === 0}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />

              {selectedServices.length > 0 && (
                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Selected Services
                    </Typography>
                    {selectedServices.map((svc) => (
                      <Box
                        key={svc.id}
                        display="flex"
                        justifyContent="space-between"
                        py={0.5}
                      >
                        <Typography>{svc.name}</Typography>
                        <Typography fontWeight="bold">£{svc.price.toFixed(2)}</Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="h6">Total</Typography>
                      <Typography
                        variant="h6"
                        sx={{ color: orgConfig.primary_color }}
                      >
                        £{calculateTotal().toFixed(2)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Paper>

            {/* Notes */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional Notes (Optional)"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any special requests or information about your vehicle..."
              />
            </Paper>

            {/* Terms & Conditions */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    name="termsAccepted"
                    color="primary"
                  />
                }
                label={
                  <Typography component="span">
                    I have read and accept the{' '}
                    <Link
                      to={`/book/${orgCode}/terms`}
                      target="_blank"
                      style={{ color: orgConfig.primary_color || '#1976d2' }}
                    >
                      terms and conditions
                    </Link>
                    {' '}for {orgConfig.name}
                  </Typography>
                }
              />
            </Paper>

            {/* Submit */}
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting || selectedServices.length === 0 || !formData.termsAccepted}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                backgroundColor: orgConfig.primary_color,
                '&:hover': {
                  backgroundColor: orgConfig.primary_color,
                  filter: 'brightness(0.9)',
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                `Book Now - £${calculateTotal().toFixed(2)}`
              )}
            </Button>

            {/* Contact Info */}
            <Box textAlign="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                Questions? Contact us at{' '}
                {orgConfig.phone && <span>{orgConfig.phone}</span>}
                {orgConfig.phone && orgConfig.contact_email && ' or '}
                {orgConfig.contact_email && (
                  <a href={`mailto:${orgConfig.contact_email}`}>
                    {orgConfig.contact_email}
                  </a>
                )}
              </Typography>
            </Box>
          </form>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

export default PublicBookingForm;
